/**
 * Cliente do backoffice: access token (10 min) só em memória; o refresh vive
 * num cookie HttpOnly em /api/auth (o JavaScript nunca o vê). Um 401 tenta
 * renovar uma vez e repete o pedido.
 */
let token: string | null = null;
let refreshing: Promise<boolean> | null = null;

export class BoError extends Error {
  constructor(readonly code: string, message: string, readonly status: number, readonly issues?: { path: string; message: string }[]) { super(message); }
}

export interface Session { user: { id: string; name: string; email: string }; tenant: { id: string; name: string; role: 'owner' | 'manager' | 'cashier' } }

async function raw(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    // content-type só com corpo: o Fastify recusa (400) um JSON vazio — a
    // renovação da sessão e o "Novo código" são POST sem corpo.
    headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), 'x-requested-with': 'makila-pos', ...(token ? { authorization: `Bearer ${token}` } : {}), ...init.headers },
  });
}

async function parse<T>(r: Response): Promise<T> {
  const b = await r.json().catch(() => null);
  if (!r.ok) {
    const msg = b?.issues?.length ? b.issues.map((i: { message: string }) => i.message).join('; ') : b?.message;
    throw new BoError(b?.code ?? `http_${r.status}`, msg ?? `Erro ${r.status}`, r.status, b?.issues);
  }
  return b as T;
}

export async function refresh(): Promise<Session | null> {
  const r = await raw('/auth/refresh', { method: 'POST' });
  if (!r.ok) { token = null; return null; }
  const b = await r.json();
  token = b.accessToken;
  return { user: b.user, tenant: b.tenant };
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let r = await raw(path, init);
  if (r.status === 401 && !path.startsWith('/auth/')) {
    refreshing ??= refresh().then((s) => !!s).finally(() => { refreshing = null; });
    if (await refreshing) r = await raw(path, init);
  }
  return parse<T>(r);
}

export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export async function login(email: string, password: string): Promise<Session> {
  const b = await post<{ accessToken: string } & Session>('/auth/login', { email, password });
  token = b.accessToken;
  return { user: b.user, tenant: b.tenant };
}

export async function signup(body: unknown): Promise<Session> {
  const b = await post<{ accessToken: string } & Session>('/auth/signup', body);
  token = b.accessToken;
  return { user: b.user, tenant: b.tenant };
}

export async function logout(): Promise<void> {
  await raw('/auth/logout', { method: 'POST' }).catch(() => {});
  token = null;
}

/** Entrada pelo "Abrir POS" do Makila: o código veio no fragmento do endereço (#code=…). */
export async function ssoLogin(code: string): Promise<Session> {
  const b = await post<{ accessToken: string } & Session>('/auth/sso', { code });
  token = b.accessToken;
  return { user: b.user, tenant: b.tenant };
}
